import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSignMessage } from "wagmi";
import { fetchClient } from "@/lib/api-client";
import { buildStatementVoteMessage } from "@/lib/statement/voteMessage";

interface StatementVoteBody {
  support: "yes" | "no";
  source: "wallet" | "stake";
  amount: string;
  signature: string;
  message: string;
}

export function useCastStatementVote(statementId: string, index: number) {
  const { signMessageAsync } = useSignMessage();
  const post = useMutation({
    mutationFn: async ({
      params,
      body,
    }: {
      params: { path: { id: string } };
      body: StatementVoteBody;
    }) => {
      const { data, error } = await (
        fetchClient as unknown as {
          POST: (
            path: "/statements/{id}/votes",
            init: {
              params: { path: { id: string } };
              body: StatementVoteBody;
            },
          ) => Promise<{ data?: unknown; error?: unknown }>;
        }
      ).POST("/statements/{id}/votes", { params, body });
      if (error) throw error;
      return data;
    },
  });
  const qc = useQueryClient();

  return {
    isPending: post.isPending,
    castAsync: async (
      support: "yes" | "no",
      source: "wallet" | "stake",
      amount: string, // wei
      wallet: string,
    ) => {
      const message = buildStatementVoteMessage({
        index,
        support,
        source,
        amount,
        wallet,
      });
      const signature = await signMessageAsync({ message });
      await post.mutateAsync({
        params: { path: { id: statementId } },
        body: { support, source, amount, signature, message },
      });
      qc.invalidateQueries({
        queryKey: ["get", "/statements/{id}/votes/mine"],
      });
      qc.invalidateQueries({
        queryKey: ["get", "/statements/{id}/votes/budget"],
      });
    },
  };
}
